/**
 * FederatedDashboardPage - Federated Governance Dashboard (EQ-PROV-007)
 *
 * Three-Way Authority Minimum: min(capsule, policy, maturity)
 * Displays cross-entity dispatch statistics, tier distribution, and audit trail.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useStatus } from '../../contexts/StatusProvider';
import { Make_Authorized_API_Call } from '@equilateral/ui-shared';
import { API_ENDPOINTS } from '../../services/api/endpoints';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { Card, Badge, Table } from 'flowbite-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditSummaryRecord {
  origin_company_id: string;
  origin_company_name: string;
  target_company_id: string;
  target_company_name: string;
  total_dispatches: number;
  passed: number;
  held: number;
  denied: number;
  avg_effective_tier: number;
  last_dispatch_at: string;
}

interface AuditRecord {
  audit_id: string;
  origin_company_id: string;
  target_company_id: string;
  capsule_tier: number;
  policy_ceiling: number;
  maturity_ceiling: number;
  effective_tier: number;
  gate_outcome: 'PASS' | 'HOLD' | 'DENY';
  scope_elevation: string;
  action_category: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIER_COLORS: Record<string, string> = {
  T1: '#22c55e',
  T2: '#3b82f6',
  T3: '#eab308',
  T4: '#f97316',
  T5: '#ef4444',
};

const OUTCOME_COLOR: Record<string, 'success' | 'warning' | 'failure'> = {
  PASS: 'success',
  HOLD: 'warning',
  DENY: 'failure',
};

const SCOPE_COLORS: Record<string, 'info' | 'purple' | 'pink' | 'gray'> = {
  ELEVATED: 'purple',
  RESTRICTED: 'pink',
  NORMAL: 'info',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FederatedDashboardPage: React.FC = () => {
  const { Client_ID } = useStatus();

  const [summaryRecords, setSummaryRecords] = useState<AuditSummaryRecord[]>([]);
  const [auditRecords, setAuditRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Data Fetching
  // -------------------------------------------------------------------------

  const fetchData = useCallback(async () => {
    if (!Client_ID) return;

    setLoading(true);
    setError(null);

    try {
      const [summaryRes, auditRes] = await Promise.all([
        Make_Authorized_API_Call(
          `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_AUDIT_SUMMARY}?Client_ID=${Client_ID}`,
          'GET'
        ),
        Make_Authorized_API_Call(
          `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_AUDIT_GET}?Client_ID=${Client_ID}&limit=20`,
          'GET'
        ),
      ]);

      if (!summaryRes?.success) {
        throw new Error((summaryRes as any)?.error_details || 'Failed to fetch audit summary');
      }
      if (!auditRes?.success) {
        throw new Error((auditRes as any)?.error_details || 'Failed to fetch audit records');
      }

      setSummaryRecords(summaryRes.data?.Records ?? []);
      setAuditRecords(auditRes.data?.Records ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [Client_ID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // -------------------------------------------------------------------------
  // Derived Data
  // -------------------------------------------------------------------------

  const totalDispatches = summaryRecords.reduce((sum, r) => sum + r.total_dispatches, 0);
  const totalPassed = summaryRecords.reduce((sum, r) => sum + r.passed, 0);
  const passRate = totalDispatches > 0 ? ((totalPassed / totalDispatches) * 100).toFixed(1) : '0.0';
  const avgEffectiveTier =
    summaryRecords.length > 0
      ? (summaryRecords.reduce((sum, r) => sum + r.avg_effective_tier, 0) / summaryRecords.length).toFixed(2)
      : '0.00';
  const activePolicyPairs = summaryRecords.length;

  // Bar chart data
  const barChartData = summaryRecords.map((r) => ({
    name: `${r.origin_company_name} \u2192 ${r.target_company_name}`,
    passed: r.passed,
    held: r.held,
    denied: r.denied,
  }));

  // Pie chart data — distribution of effective_tier from audit records
  const tierCounts: Record<string, number> = {};
  auditRecords.forEach((r) => {
    const key = `T${r.effective_tier}`;
    tierCounts[key] = (tierCounts[key] || 0) + 1;
  });
  const pieChartData = Object.entries(tierCounts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tier, count]) => ({ name: tier, value: count }));

  // -------------------------------------------------------------------------
  // Loading State
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <div className="h-8 w-80 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-5 w-96 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        </div>
        <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Error State
  // -------------------------------------------------------------------------

  if (error) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <div className="text-red-500 dark:text-red-400 text-lg font-medium">
            Failed to load dashboard
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md text-center">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Federated Governance Dashboard
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Three-Way Authority Minimum &mdash; min(capsule, policy, maturity)
        </p>
      </div>

      {/* Section A: Summary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Dispatches</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalDispatches}</p>
        </Card>
        <Card>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pass Rate</p>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">{passRate}%</p>
        </Card>
        <Card>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Average Effective Tier
          </p>
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{avgEffectiveTier}</p>
        </Card>
        <Card>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Active Policy Pairs
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{activePolicyPairs}</p>
        </Card>
      </div>

      {/* Section B + C: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section B: Dispatch Volume by Entity Pair */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Dispatch Volume by Entity Pair
          </h2>
          {barChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="passed" stackId="a" fill="#22c55e" name="Passed" />
                <Bar dataKey="held" stackId="a" fill="#eab308" name="Held" />
                <Bar dataKey="denied" stackId="a" fill="#ef4444" name="Denied" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 text-center py-16">
              No dispatch data available
            </p>
          )}
        </Card>

        {/* Section C: Three-Way Min Distribution */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Three-Way Min Distribution
          </h2>
          {pieChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                >
                  {pieChartData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={TIER_COLORS[entry.name] || '#6b7280'}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 text-center py-16">
              No tier distribution data available
            </p>
          )}
        </Card>
      </div>

      {/* Section D: Recent Audit Trail */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Recent Audit Trail
        </h2>
        {auditRecords.length > 0 ? (
          <div className="overflow-x-auto">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>Time</Table.HeadCell>
                <Table.HeadCell>Origin</Table.HeadCell>
                <Table.HeadCell>Target</Table.HeadCell>
                <Table.HeadCell>Capsule Tier</Table.HeadCell>
                <Table.HeadCell>Policy Ceiling</Table.HeadCell>
                <Table.HeadCell>Maturity Ceiling</Table.HeadCell>
                <Table.HeadCell>Effective Tier</Table.HeadCell>
                <Table.HeadCell>Gate Outcome</Table.HeadCell>
                <Table.HeadCell>Scope</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {auditRecords.map((record) => (
                  <Table.Row
                    key={record.audit_id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <Table.Cell className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {new Date(record.created_at).toLocaleString()}
                    </Table.Cell>
                    <Table.Cell className="text-sm">{record.origin_company_id}</Table.Cell>
                    <Table.Cell className="text-sm">{record.target_company_id}</Table.Cell>
                    <Table.Cell className="text-center font-mono">T{record.capsule_tier}</Table.Cell>
                    <Table.Cell className="text-center font-mono">T{record.policy_ceiling}</Table.Cell>
                    <Table.Cell className="text-center font-mono">T{record.maturity_ceiling}</Table.Cell>
                    <Table.Cell className="text-center font-mono font-semibold">
                      T{record.effective_tier}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={OUTCOME_COLOR[record.gate_outcome] || 'gray'}>
                        {record.gate_outcome}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={SCOPE_COLORS[record.scope_elevation] || 'gray'}>
                        {record.scope_elevation}
                      </Badge>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <p className="text-gray-400 dark:text-gray-500 text-center py-8">
            No audit records available
          </p>
        )}
      </Card>
    </div>
  );
};
